// The chart colour system (DEV-2432 §3).
//
// One categorical palette of exactly ten colours, in this order, for EVERY panel
// and every chart type: rank 1 is always the brand purple, rank 2 always lime,
// and so on. No per-slot rotation (the ticket rules it out) and no interpolated
// in-between colour — ten colours IS the ten-category cap, so a panel can never
// need an eleventh.
//
// Two colour sets sit outside the sequence and must stay put:
//   • Status is the only dimension whose VALUES own a colour: Valid is green,
//     Suspicious amber, Bad red — as a bar dimension, a donut variable or the
//     stack-by variable alike. The map is keyed by the raw value, never by rank.
//   • The rolled-up "Other" bucket is always the neutral grey.
//
// Token status (design-system 0.39.0): `--cg-primary-500`, `--cg-lemon-100`,
// `--cg-chart-cerulean-2`, `--cg-chart-magenta-3` and `--cg-chart-other` exist;
// the other six categorical colours are not tokens yet. They are hard-coded
// here in the ticket's own order so the chart cannot drift from the spec; swap
// to `--cg-chart-*` tokens once the DS ships them.

export const CATEGORICAL_PALETTE: readonly string[] = [
	'#554BFD', // 1 purple (brand)
	'#BAE101', // 2 lime (lemon 100)
	'#1584D9', // 3 blue
	'#E23A9B', // 4 pink
	'#0EA5B8', // 5 cyan
	'#B07CFF', // 6 violet
	'#2E2A9E', // 7 indigo
	'#C33FD1', // 8 orchid
	'#8E1F63', // 9 plum
	'#2B6BE8' // 10 azure
]

/** Neutral grey for the rolled-up "Other" bucket (`--cg-chart-other`). */
export const OTHER_COLOR = '#C8C9CE'

// Click veracity colours, keyed by the raw status code the engine returns (and
// by the display name, so a snapshot row that only carries the label resolves).
export const STATUS_COLORS: Record<string, string> = {
	GOOD: '#17B26A',
	VALID: '#17B26A',
	SUSPICIOUS: '#F79009',
	BAD: '#F04438'
}

/** The colour for the category at `rank` (0-based) in the ten-colour sequence. */
export const categoricalColor = (rank: number): string =>
	CATEGORICAL_PALETTE[
		((rank % CATEGORICAL_PALETTE.length) + CATEGORICAL_PALETTE.length) %
			CATEGORICAL_PALETTE.length
	]

/** The fixed colour of a Status value, or undefined for any other value. */
export const statusColor = (rawValue: string): string | undefined =>
	STATUS_COLORS[String(rawValue ?? '').toUpperCase()]

/** True when a dimension's values ARE the colour (Status only). */
export const isSemanticDimension = (dimensionId: string | undefined): boolean =>
	dimensionId === 'status'

/**
 * The colour of one category: Status values take their semantic colour, the
 * "Other" bucket takes grey, everything else takes its rank in the sequence.
 */
export const colorFor = (
	dimensionId: string | undefined,
	rawValue: string,
	rank: number
): string => {
	if (isSemanticDimension(dimensionId))
		return statusColor(rawValue) ?? categoricalColor(rank)
	return categoricalColor(rank)
}
